import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Send, Loader2, Monitor, Tablet, Smartphone,
} from "lucide-react";
import { api, BUILD_SESSION } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// ── Palette presets (copied from Preview.jsx) ────────────────────────────────
const PALETTES = [
  {
    id: "burnt_sienna",
    name: "Burnt sienna",
    desc: "Warm editorial",
    primary: "#d4724a", secondary: "#f5a572", background: "#fffbf5", foreground: "#1a1a1a",
  },
  {
    id: "midnight_ink",
    name: "Midnight ink",
    desc: "Dark luxury",
    primary: "#d4a76a", secondary: "#8b6f47", background: "#0f0f0f", foreground: "#f5f5f5",
  },
  {
    id: "forest_cream",
    name: "Forest & cream",
    desc: "Earthy organic",
    primary: "#2d5a3d", secondary: "#4a7a5a", background: "#f5f9f5", foreground: "#1a1a1a",
  },
  {
    id: "ocean_slate",
    name: "Ocean slate",
    desc: "Cool modern",
    primary: "#2b6f9f", secondary: "#4a8fb5", background: "#f0f5f9", foreground: "#1a2a3a",
  },
  {
    id: "deep_rouge",
    name: "Deep rouge",
    desc: "Bold romantic",
    primary: "#8b3a3a", secondary: "#c96b6b", background: "#faf6f6", foreground: "#1a1a1a",
  },
  {
    id: "obsidian_lime",
    name: "Obsidian & lime",
    desc: "Street energy",
    primary: "#c8ff00", secondary: "#a8d900", background: "#0a0a0a", foreground: "#f5f5f5",
  },
  {
    id: "soft_blossom",
    name: "Soft blossom",
    desc: "Pretty romantic",
    primary: "#d97a91", secondary: "#f5b6cc", background: "#fef9f7", foreground: "#1a1a1a",
  },
  {
    id: "pure_minimal",
    name: "Pure minimal",
    desc: "Clean & sharp",
    primary: "#1a1a1a", secondary: "#333333", background: "#ffffff", foreground: "#1a1a1a",
  },
];

function paletteInstruction(p) {
  return `Switch the color palette to "${p.name}" — set primary to ${p.primary}, secondary to ${p.secondary}, background to ${p.background}, foreground/text to ${p.foreground}. Keep everything else the same.`;
}

// ── Credit gate modal (copied from Preview.jsx) ───────────────────────────────
function CreditGateModal({ slug, onClose }) {
  const [loading, setLoading] = useState(false);
  const topup = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/tenants/${slug}/iterate-topup`, {}, { headers: { origin: window.location.origin } });
      window.location.href = r.data.url;
    } catch {
      toast.error("Couldn't start checkout — try again");
      setLoading(false);
    }
  };
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "36px 32px", maxWidth: 440, width: "100%",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
      }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>✦</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Ready for more?</h2>
        <p style={{ color: "#555", fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
          You've used your free refinement. Add <strong>$5 for 10 more prompts</strong> — that's plenty to dial in exactly what you want.
        </p>
        <div style={{
          background: "#f9f7f5", border: "1px solid #e8e3dc", borderRadius: 10,
          padding: "14px 16px", marginBottom: 20, fontSize: 13, color: "#666", lineHeight: 1.6,
        }}>
          <strong style={{ color: "#1a1a1a" }}>A tip:</strong> Be intentional with each prompt. Describe the whole vibe shift, not just one thing. Most storefronts land in 2–3 prompts total.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={topup} disabled={loading} style={{ flex: 1 }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Add 10 prompts — $5
          </Button>
          <Button variant="outline" onClick={onClose} style={{ flexShrink: 0 }}>Not now</Button>
        </div>
        <p style={{ fontSize: 11, color: "#aaa", marginTop: 14, textAlign: "center" }}>
          No account needed. Credits are saved to this storefront.
        </p>
      </div>
    </div>
  );
}

// ── Chat bubble ───────────────────────────────────────────────────────────────
function ChatBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div style={{
      display: "flex",
      justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom: 10,
    }}>
      <div style={{
        maxWidth: "82%",
        padding: "9px 13px",
        borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
        background: isUser ? "#1a1a1a" : "#2a2a2a",
        color: isUser ? "#f5f5f5" : "#c0c0c0",
        fontSize: 13,
        lineHeight: 1.5,
      }}>
        {text}
      </div>
    </div>
  );
}

// ── Thinking bubble ───────────────────────────────────────────────────────────
function ThinkingBubble() {
  return (
    <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
      <div style={{
        padding: "9px 13px",
        borderRadius: "14px 14px 14px 4px",
        background: "#2a2a2a",
        color: "#888",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}>
        <span style={{ animation: "pulse 1.5s ease-in-out infinite" }}>Thinking</span>
        <span style={{ letterSpacing: 2, animation: "pulse 1.5s ease-in-out 0.3s infinite" }}>…</span>
      </div>
    </div>
  );
}

// ── Device widths ─────────────────────────────────────────────────────────────
const DEVICES = [
  { id: "mobile",  label: "Mobile",  icon: Smartphone, width: 375 },
  { id: "tablet",  label: "Tablet",  icon: Tablet,     width: 768 },
  { id: "desktop", label: "Desktop", icon: Monitor,    width: null },
];

// ── Quick action chips ────────────────────────────────────────────────────────
const VIBE_CHIPS = [
  { label: "Warmer",            text: "Make the overall tone warmer — richer colors, softer edges." },
  { label: "Darker",            text: "Go darker and more dramatic. Deep background, high contrast." },
  { label: "More minimal",      text: "Strip it back — clean white space, minimal decoration, refined typography." },
  { label: "Bold & energetic",  text: "Make it bold and high-energy — loud colors, strong type, street food vibes." },
  { label: "Elegant & refined", text: "Make it feel elegant and refined — upscale, sophisticated, fine dining." },
];

// ── Empty state example chips ─────────────────────────────────────────────────
const EXAMPLE_CHIPS = [
  "Make it darker and more dramatic",
  "Switch to a bold street food vibe",
  "Make it feel like a high-end steakhouse",
];

// ─────────────────────────────────────────────────────────────────────────────
export default function Build() {
  const { slug } = useParams();

  const [tenant, setTenant]               = useState(null);
  const [prompt, setPrompt]               = useState("");
  const [busy, setBusy]                   = useState(false);
  const [messages, setMessages]           = useState([]);  // { role: "user"|"ai", text: string }
  const [previewVersion, setPreviewVersion] = useState(0);
  const [device, setDevice]               = useState("desktop");
  const [iterateCount, setIterateCount]   = useState(0);
  const [iterateCredits, setIterateCredits] = useState(0);
  const [showCreditGate, setShowCreditGate] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const textareaRef  = useRef(null);
  const chatEndRef   = useRef(null);

  // Load tenant on mount
  const load = async () => {
    try {
      const t = await api.get(`/tenants/${slug}`);
      setTenant(t.data);
      setLoadError(false);
      setIterateCount(t.data.iterate_count ?? 0);
      setIterateCredits(t.data.iterate_credits ?? 0);
      BUILD_SESSION.set(slug, t.data.name);
    } catch {
      setLoadError(true);
      toast.error("Couldn't load storefront");
    }
  };

  useEffect(() => {
    load();
    if (window.location.search.includes("topup=success")) {
      toast.success("Credits added! You have 10 more refinements.");
    }
    /* eslint-disable-next-line */
  }, [slug]);

  // Scroll chat to bottom after each new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  // ── Iterate call (mirrors Preview.jsx exactly) ──────────────────────────────
  const applyInstruction = async (instruction) => {
    if (!instruction.trim()) return;
    if (iterateCount >= 1 && iterateCredits <= 0) {
      setShowCreditGate(true);
      return;
    }
    setMessages(m => [...m, { role: "user", text: instruction }]);
    setPrompt("");
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${slug}/iterate`, { instruction });
      const applied = r.data?.applied ?? {};
      // Build a human-readable summary from the diff keys the LLM returned
      const summaryParts = [];
      if (applied.theme)  summaryParts.push("colors & fonts updated");
      if (applied.layout) summaryParts.push("layout changed");
      if (applied.copy)   summaryParts.push("copy rewritten");
      if (applied.brand)  summaryParts.push("brand voice updated");
      const summary = summaryParts.length > 0
        ? `Applied — ${summaryParts.join(", ")}.`
        : "Applied. The storefront has been updated.";
      setMessages(m => [...m, { role: "ai", text: summary }]);
      if (r.data.iterate_count  !== undefined) setIterateCount(r.data.iterate_count);
      if (r.data.iterate_credits !== undefined) setIterateCredits(r.data.iterate_credits);
      setPreviewVersion(v => v + 1);
      await load();
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowCreditGate(true);
        // Remove the user message we optimistically added
        setMessages(m => m.slice(0, -1));
      } else {
        toast.error(e?.response?.data?.detail || "Couldn't apply that change");
        setMessages(m => m.slice(0, -1));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = () => applyInstruction(prompt);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
  };

  const prefill = (text) => {
    setPrompt(text);
    textareaRef.current?.focus();
  };

  // ── Computed preview iframe width ───────────────────────────────────────────
  const activeDevice = DEVICES.find(d => d.id === device);
  const iframeStyle = {
    width:  activeDevice.width ? `${activeDevice.width}px` : "100%",
    height: "100%",
    border: "none",
    display: "block",
    margin: "0 auto",
    transition: "width 200ms ease",
  };

  if (loadError) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#111", color: "#f5f5f5", padding: 24 }}>
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <h1 style={{ fontSize: 24, marginBottom: 10 }}>This build is no longer available</h1>
          <p style={{ color: "#aaa", lineHeight: 1.5, marginBottom: 20 }}>
            The saved browser link may be stale. Start a new preview or return to the landing page.
          </p>
          <Link to="/" style={{ display: "inline-block", background: "#d4724a", color: "#fff", padding: "9px 16px", borderRadius: 7, textDecoration: "none", fontWeight: 600 }}>
            Start a new preview
          </Link>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div style={{
        height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "#111", color: "#666", fontSize: 14,
      }}>
        Loading build studio…
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100vh",
      background: "#111", color: "#f5f5f5", overflow: "hidden",
    }}>
      {showCreditGate && (
        <CreditGateModal
          slug={slug}
          onClose={() => setShowCreditGate(false)}
        />
      )}

      {/* ── 48px Header ─────────────────────────────────────────────────────── */}
      <header style={{
        height: 48, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 16px",
        background: "#0d0d0d",
        borderBottom: "1px solid #222",
        gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <Link
            to={`/preview/${slug}`}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              color: "#888", fontSize: 13, textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <ArrowLeft style={{ width: 14, height: 14 }}/>
            Back
          </Link>
          <span style={{ color: "#333", fontSize: 13 }}>·</span>
          <span style={{
            fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase", color: "#888",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {tenant.name} · build studio
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {iterateCount >= 1 && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
              background: iterateCredits > 0 ? "#0f2a14" : "#2a1800",
              color: iterateCredits > 0 ? "#4ade80" : "#f59e0b",
              border: `1px solid ${iterateCredits > 0 ? "#166534" : "#92400e"}`,
            }}>
              {iterateCredits > 0 ? `${iterateCredits} prompts left` : "0 prompts left"}
            </span>
          )}
          {/* Phone-agent test CTA removed pre-launch — the dynamic AI phone test
              is deferred. Backend (DeepSeekBridge, /phone/simulate-call, PhoneSim
              route) is intentionally kept for the future build. */}
          <Link to={`/onboard/${slug}`}>
            <Button size="sm" style={{
              background: "var(--color-primary, #d4724a)",
              color: "#fff", border: "none", fontSize: 12,
              display: "flex", alignItems: "center", gap: 5,
            }}>
              Activate <ArrowRight style={{ width: 13, height: 13 }}/>
            </Button>
          </Link>
        </div>
      </header>

      {/* ── Body: left panel + right panel ──────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

        {/* ── LEFT PANEL ──────────────────────────────────────────────────── */}
        <div style={{
          width: 380, flexShrink: 0,
          display: "flex", flexDirection: "column",
          background: "#161616",
          borderRight: "1px solid #222",
          overflow: "hidden",
        }}>

          {/* Chat history */}
          <div style={{ flex: 1, overflowY: "auto", padding: "14px 14px 0 14px" }}>
            {messages.length === 0 && !busy ? (
              /* Empty state */
              <div style={{ paddingTop: 24, paddingBottom: 12 }}>
                <p style={{ fontSize: 12, color: "#555", marginBottom: 14, textAlign: "center" }}>
                  No changes yet. Try one of these:
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {EXAMPLE_CHIPS.map((chip, i) => (
                    <button
                      key={i}
                      onClick={() => prefill(chip)}
                      style={{
                        textAlign: "left", padding: "10px 12px",
                        background: "#1e1e1e", border: "1px solid #2a2a2a",
                        borderRadius: 10, color: "#aaa", fontSize: 13,
                        cursor: "pointer", lineHeight: 1.4,
                        transition: "border-color 100ms, color 100ms",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#555"; e.currentTarget.style.color = "#ddd"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = "#2a2a2a"; e.currentTarget.style.color = "#aaa"; }}
                    >
                      "{chip}"
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <ChatBubble key={i} role={msg.role} text={msg.text} />
                ))}
                {busy && <ThinkingBubble />}
              </>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Quick action chips */}
          <div style={{
            padding: "10px 14px 4px",
            borderTop: "1px solid #1e1e1e",
          }}>
            {/* Palette chips — horizontal scroll */}
            <div style={{
              display: "flex", gap: 6, overflowX: "auto",
              paddingBottom: 6,
              scrollbarWidth: "none",
            }}>
              {PALETTES.map(p => (
                <button
                  key={p.id}
                  title={`${p.name} — ${p.desc}`}
                  onClick={() => prefill(paletteInstruction(p))}
                  style={{
                    flexShrink: 0,
                    display: "flex", alignItems: "center", gap: 5,
                    padding: "4px 8px",
                    background: "#1e1e1e", border: "1px solid #2a2a2a",
                    borderRadius: 20, cursor: "pointer",
                    fontSize: 11, color: "#aaa",
                    transition: "border-color 100ms",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#555"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#2a2a2a"}
                >
                  <span style={{
                    width: 10, height: 10, borderRadius: "50%",
                    background: p.primary, flexShrink: 0,
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}/>
                  {p.name}
                </button>
              ))}
            </div>

            {/* Vibe chips */}
            <div style={{
              display: "flex", gap: 6, overflowX: "auto",
              paddingBottom: 4,
              scrollbarWidth: "none",
            }}>
              {VIBE_CHIPS.map((chip, i) => (
                <button
                  key={i}
                  onClick={() => prefill(chip.text)}
                  style={{
                    flexShrink: 0,
                    padding: "4px 10px",
                    background: "#1e1e1e", border: "1px solid #2a2a2a",
                    borderRadius: 20, cursor: "pointer",
                    fontSize: 11, color: "#aaa",
                    transition: "border-color 100ms, color 100ms",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#555"; e.currentTarget.style.color = "#ddd"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#2a2a2a"; e.currentTarget.style.color = "#aaa"; }}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>

          {/* Textarea + send */}
          <div style={{ padding: "8px 14px 14px", borderTop: "1px solid #1e1e1e" }}>
            <div style={{ position: "relative" }}>
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
                placeholder="Describe the vibe shift — colors, energy, layout, copy..."
                style={{
                  width: "100%", padding: "11px 46px 11px 12px",
                  background: "#1e1e1e", border: "1.5px solid #2a2a2a",
                  borderRadius: 10, color: "#f0f0f0", fontSize: 13,
                  lineHeight: 1.5, resize: "none", fontFamily: "inherit",
                  outline: "none", boxSizing: "border-box",
                  transition: "border-color 120ms",
                }}
                onFocus={e => e.target.style.borderColor = "#555"}
                onBlur={e => e.target.style.borderColor = "#2a2a2a"}
              />
              <button
                onClick={handleSubmit}
                disabled={busy || !prompt.trim()}
                title="Apply (Cmd+Enter)"
                style={{
                  position: "absolute", right: 8, bottom: 8,
                  width: 30, height: 30, borderRadius: 7,
                  background: busy || !prompt.trim() ? "#2a2a2a" : "var(--color-primary, #d4724a)",
                  border: "none",
                  cursor: busy || !prompt.trim() ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 120ms",
                }}
              >
                {busy
                  ? <Loader2 style={{ width: 14, height: 14, color: "#666", animation: "spin 1s linear infinite" }}/>
                  : <Send style={{ width: 13, height: 13, color: busy || !prompt.trim() ? "#555" : "#fff" }}/>
                }
              </button>
            </div>
            <p style={{ fontSize: 10, color: "#444", marginTop: 5 }}>
              Cmd+Enter to apply
            </p>
          </div>
        </div>

        {/* ── RIGHT PANEL ─────────────────────────────────────────────────── */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          background: "#0a0a0a", overflow: "hidden",
          position: "relative",
        }}>
          {/* iframe container */}
          <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
            {/* Busy overlay — pulsing border signal */}
            {busy && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 10,
                pointerEvents: "none",
                boxShadow: "inset 0 0 0 2px var(--color-primary, #d4724a)",
                animation: "iframe-pulse 1.2s ease-in-out infinite",
              }}>
                <style>{`
                  @keyframes iframe-pulse {
                    0%, 100% { opacity: 0.3; }
                    50%       { opacity: 1; }
                  }
                  @keyframes pulse {
                    0%, 100% { opacity: 0.4; }
                    50%       { opacity: 1; }
                  }
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                  }
                `}</style>
              </div>
            )}
            <iframe
              key={previewVersion}
              src={`/t/${slug}`}
              title="Storefront preview"
              sandbox="allow-scripts allow-same-origin allow-forms"
              style={iframeStyle}
            />
          </div>

          {/* Device toggle bar — 32px at bottom */}
          <div style={{
            height: 40, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 4,
            background: "#0d0d0d",
            borderTop: "1px solid #1a1a1a",
          }}>
            {DEVICES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setDevice(id)}
                title={label}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "4px 12px", borderRadius: 6, border: "none",
                  cursor: "pointer", fontSize: 11, fontWeight: 600,
                  background: device === id ? "#222" : "transparent",
                  color: device === id ? "#f5f5f5" : "#555",
                  transition: "background 100ms, color 100ms",
                }}
              >
                <Icon style={{ width: 12, height: 12 }}/>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
